import { t } from '../i18n';
import { formatCurrency } from './countriesHelpers';
import { logger } from './logger';

export const roundMarkupPercentage = (value?: number | string | null): number => {
  const parsedValue = parseFloat(String(value ?? ''));

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return 0;
  }

  return Math.round((parsedValue + Number.EPSILON) * 100) / 100;
};

export const formatMarkupPercent = (value?: number | string | null): string => `${roundMarkupPercentage(value)}%`;

const MARKUP_UNITS = { PERCENT: 'percent', CURRENCY: 'currency' } as const;

type MarkupUnit = (typeof MARKUP_UNITS)[keyof typeof MARKUP_UNITS];

export interface IMarkup {
  value: number | null;
  // Server-shaped: a unit this build does not know still parses, so the two above are a hint, not a promise.
  unit: MarkupUnit | (string & {});
}

export interface IMarkupSource {
  markup?: IMarkup | null;
  markup_percentage?: number | string | null;
}

// A negative markup is a deduction, not an absence; a missing or unreadable one is blank, since any number here
// would read as a real percentage. formatMarkupPercent still clamps those to 0 for agency pricing.
const formatSignedPercent = (value?: number | string | null): string => {
  if (value == null || value === '') return '';

  const parsed = parseFloat(String(value));

  if (!Number.isFinite(parsed)) {
    // Once per category, not per value: formatting runs on every paint of a rate table.
    logger.errorOnce('markup.unreadable_percentage', `Unreadable markup percentage "${value}"`);

    return '';
  }

  const rounded = Math.sign(parsed) * (Math.round((Math.abs(parsed) + Number.EPSILON) * 100) / 100);

  return `${rounded}%`;
};

// Responses that carry only `markup_percentage` have no `markup`; that field is then read as a percentage.
export const formatMarkupValue = ({ markup, markup_percentage }: IMarkupSource): string => {
  if (!markup) return formatSignedPercent(markup_percentage);

  if (markup.unit === MARKUP_UNITS.PERCENT) return formatSignedPercent(markup.value);

  // A currency markup is a per-hour amount; a percentage is a ratio and carries no unit of time.
  if (markup.unit === MARKUP_UNITS.CURRENCY) {
    const amount = Number(markup.value);

    // Money with no amount is not £0.00/hr: that would read as a real, zero hourly markup.
    if (markup.value == null || !Number.isFinite(amount)) return '';

    return t('dash.common.units.per_hour', { value: formatCurrency(amount) });
  }

  // A raw number would read as a percentage or money, so an unknown unit is left blank.
  logger.errorOnce(`markup.unknown_unit.${markup.unit}`, `Unknown markup unit "${markup.unit}"`);

  return '';
};

export const formatMarkupInputValue = (value?: number | string, info?: { userTyping?: boolean }): string => {
  const stringValue = String(value ?? '');

  if (info?.userTyping || stringValue === '') {
    return stringValue;
  }

  const parsedValue = parseFloat(stringValue);

  return Number.isFinite(parsedValue) ? String(parsedValue) : stringValue;
};
